import { createSignal, For, Show } from "solid-js";
import { CheckCircle2, XCircle, Info, AlertTriangle, type LucideProps } from "lucide-solid";
import type { Component } from "solid-js";

interface ToastMessage {
  id: number;
  text: string;
  type: "success" | "error" | "info" | "warning";
}

const [toasts, setToasts] = createSignal<ToastMessage[]>([]);
let nextId = 0;

export function showToast(text: string, type: ToastMessage["type"] = "info") {
  const id = nextId++;
  setToasts((prev) => [...prev, { id, text, type }]);
  setTimeout(() => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, 4000);
}

// Saturated, high-contrast backgrounds so toasts pop against the dark navy UI (the old
// "info" grey blended into the background). Each pairs a bright fill with a light ring + icon.
const typeStyles: Record<ToastMessage["type"], string> = {
  success: "bg-emerald-600 ring-emerald-300/50",
  error: "bg-red-600 ring-red-300/50",
  info: "bg-sky-600 ring-sky-300/50",
  warning: "bg-amber-500 ring-amber-200/50",
};

const typeIcons: Record<ToastMessage["type"], Component<LucideProps>> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

export default function ToastContainer() {
  return (
    <Show when={toasts().length > 0}>
      {/* Sits above the fixed price/nav bar (z-50) so it never hides behind it. */}
      <div class="fixed bottom-28 right-6 z-[60] flex flex-col gap-2">
        <For each={toasts()}>
          {(toast) => {
            const Icon = typeIcons[toast.type];
            return (
              <div
                class={`${typeStyles[toast.type]} text-white pl-4 pr-5 py-3.5 rounded-xl shadow-2xl ring-1 text-sm font-medium max-w-sm flex items-center gap-3 cursor-pointer animate-slide-in`}
                onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              >
                <Icon class="w-5 h-5 flex-shrink-0" />
                <span>{toast.text}</span>
              </div>
            );
          }}
        </For>
      </div>
    </Show>
  );
}
